import { createFileRoute } from '@tanstack/react-router';
import { FileUp } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { cn } from '@/lib/utils';
import { CardSkeleton } from '../../components/common/Skeleton';
import { AccountProposalCard } from '../../components/transactions/AccountProposalCard';
import { ImportPreviewTable } from '../../components/transactions/ImportPreviewTable';
import { RuleSuggestionRow } from '../../components/transactions/RuleSuggestionRow';
import { Button } from '../../components/ui/button';
import {
  type AccountOverride,
  type AccountProposal,
  buildConfirmImportInput,
  buildRuleSummary,
  createImportReviewStore,
  type ImportedTxn,
  type ImportReviewStoreApi,
  type PersistedReviewStateV1,
  selectPersistedReviewState,
} from '../../lib/state/importReviewStore';
import { trpc } from '../../lib/trpc';
import { useManualSave } from '../../lib/useManualSave';
import { usePageTitle } from '../../lib/usePageTitle';
import type { TxnOverride } from './-rule-application';

export const Route = createFileRoute('/import/')({
  component: ImportPage,
});

function formatRelativeTime(isoString: string): string {
  const seconds = Math.round((Date.now() - new Date(isoString).getTime()) / 1000);
  if (seconds < 10) return 'just nu';
  if (seconds < 60) return `för ${seconds} sekunder sedan`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `för ${minutes} minut${minutes === 1 ? '' : 'er'} sedan`;
  return `för ${Math.round(minutes / 60)} timm${Math.round(minutes / 60) === 1 ? 'e' : 'ar'} sedan`;
}

function ImportPage() {
  usePageTitle('Importera');
  const utils = trpc.useUtils();
  const fileRef = useRef<HTMLInputElement>(null);
  const storeRef = useRef<ImportReviewStoreApi | null>(null);
  if (!storeRef.current) {
    storeRef.current = createImportReviewStore();
  }
  const store = storeRef.current;

  const batchId = useStore(store, (state) => state.batchId);
  const transactions = useStore(store, (state) => state.transactions);
  const overrides = useStore(store, (state) => state.overrides);
  const skippedReservedCount = useStore(store, (state) => state.skippedReservedCount);
  const saveStatus = useStore(store, (state) => state.saveStatus);
  const lastSavedAt = useStore(store, (state) => state.lastSavedAt);
  const isResuming = useStore(store, (state) => state.isResuming);
  const ruleApplicationNotice = useStore(store, (state) => state.ruleApplicationNotice);
  const clearRuleApplicationNotice = useStore(store, (state) => state.clearRuleApplicationNotice);
  const setIsResuming = useStore(store, (state) => state.setIsResuming);

  const [done, setDone] = useState(false);
  const [confirmResult, setConfirmResult] = useState<{
    accountsCreated: number;
    rulesCreated: number;
    transferRulesCreated: number;
  } | null>(null);

  const membersQuery = trpc.household.get.useQuery();
  const categoriesQuery = trpc.category.listGroups.useQuery();
  const pendingImportQuery = trpc.transaction.pendingImport.useQuery();
  const importMutation = trpc.transaction.import.useMutation();
  const quickCreateMutation = trpc.category.quickCreate.useMutation({
    onSuccess: () => {
      categoriesQuery.refetch();
    },
  });
  const discardMutation = trpc.transaction.discardImport.useMutation({
    onSuccess: () => {
      pendingImportQuery.refetch();
    },
  });
  const confirmMutation = trpc.transaction.confirmImport.useMutation({
    onSuccess: async (data) => {
      await Promise.all([
        utils.transaction.pendingImport.invalidate(),
        utils.insights.overview.invalidate(),
        utils.insights.categoryTransactions.invalidate(),
        utils.recurringBill.list.invalidate(),
        utils.recurringBill.predict.invalidate(),
        utils.recurringBill.history.invalidate(),
        utils.categoryRule.list.invalidate(),
      ]);
      setConfirmResult({
        accountsCreated: data.accountsCreated,
        rulesCreated: data.rulesCreated,
        transferRulesCreated: data.transferRulesCreated,
      });
      setDone(true);
    },
  });

  const saveProgressMutation = trpc.transaction.saveProgress.useMutation();

  const { save: saveProgress } = useManualSave({
    batchId,
    getReviewState: () => selectPersistedReviewState(store.getState()),
    saveFn: (input) => saveProgressMutation.mutateAsync(input),
    onSaving: () => store.getState().markSaving(),
    onSaved: (savedAt) => store.getState().markSaved(savedAt),
    onError: () => store.getState().markSaveError(),
  });

  const members: { id: string; displayName: string }[] =
    (membersQuery.data as { members?: { id: string; displayName: string }[] } | undefined)
      ?.members ?? [];
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const hasSelectedMember = selectedMemberId !== '';

  useEffect(() => {
    if (!ruleApplicationNotice) return;
    const timeoutId = window.setTimeout(() => clearRuleApplicationNotice(), 3000);
    return () => window.clearTimeout(timeoutId);
  }, [clearRuleApplicationNotice, ruleApplicationNotice]);

  // Resume import: deferred to let loading indicator paint first
  useEffect(() => {
    if (!isResuming) return;
    const data = pendingImportQuery.data;
    if (!data?.reviewState || !data.transactions) {
      setIsResuming(false);
      return;
    }

    const reviewState: PersistedReviewStateV1 = {
      version: 1,
      transactionOverrides: (data.reviewState.transactionOverrides ?? {}) as Record<
        string,
        TxnOverride
      >,
      accountOverrides: (data.reviewState.accountOverrides ?? {}) as Record<
        string,
        AccountOverride
      >,
    };

    store.getState().hydrateSavedProgress({
      batchId: data.id,
      transactions: data.transactions as ImportedTxn[],
      accountProposals: (data.accountProposals ?? []) as AccountProposal[],
      skippedReservedCount: data.skippedReservedCount ?? 0,
      reviewState,
    });
  }, [isResuming, pendingImportQuery.data, setIsResuming, store]);

  const allCategories = useMemo(
    () =>
      (categoriesQuery.data ?? []).flatMap(
        (g: { name: string; categories: { id: string; name: string }[] }) =>
          g.categories.map((c) => ({
            id: c.id,
            label: `${g.name} / ${c.name}`,
          })),
      ),
    [categoriesQuery.data],
  );

  const allGroups = useMemo(
    () =>
      (categoriesQuery.data ?? []).map((g: { id: string; name: string }) => ({
        id: g.id,
        name: g.name,
      })),
    [categoriesQuery.data],
  );

  const quickCreateRef = useRef(quickCreateMutation.mutateAsync);
  quickCreateRef.current = quickCreateMutation.mutateAsync;

  const handleCreateCategory = useCallback(async (groupId: string, name: string) => {
    try {
      return await quickCreateRef.current({
        groupId,
        name,
      });
    } catch {
      return null;
    }
  }, []);

  const getCategoryLabel = (id: string) => allCategories.find((c) => c.id === id)?.label ?? 'Okänd';

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !hasSelectedMember) return;

    const arrayBuffer = await file.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (const b of bytes) binary += String.fromCharCode(b);
    const base64 = btoa(binary);

    const memberId = selectedMemberId === '__shared__' ? null : selectedMemberId;
    importMutation.mutate(
      {
        fileContent: base64,
        fileName: file.name,
        memberId,
      },
      {
        onSuccess: (data) => {
          store.getState().initializeFromImport({
            batchId: data.batchId,
            transactions: data.transactions as ImportedTxn[],
            accountProposals: data.accountProposals as AccountProposal[],
            skippedReservedCount: data.skippedReservedCount ?? 0,
          });
        },
      },
    );
  };

  const ruleSummary = buildRuleSummary({
    transactions,
    overrides,
    getCategoryLabel,
  });

  const handleConfirm = () => {
    const input = buildConfirmImportInput(store.getState());
    if (!input) return;
    confirmMutation.mutate(input);
  };

  if (membersQuery.isLoading || categoriesQuery.isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-4">
        <CardSkeleton lines={2} />
        <CardSkeleton lines={3} />
      </div>
    );
  }

  if (done) {
    return (
      <div className="mx-auto mt-8 max-w-sm sm:mt-16">
        <div className="rounded-2xl border border-border bg-card p-6 text-center sm:p-7">
          <span
            aria-hidden="true"
            className="mx-auto flex size-12 items-center justify-center rounded-full bg-positive-soft text-xl font-semibold text-positive"
          >
            ✓
          </span>
          <h1 className="mt-4 text-xl font-semibold tracking-tight">Import klar!</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Transaktionerna har lagts till.
            {confirmResult && (
              <>
                {confirmResult.accountsCreated > 0 && (
                  <span className="mt-1 block">
                    {confirmResult.accountsCreated} bankkonto(n) skapade.
                  </span>
                )}
                {confirmResult.rulesCreated > 0 && (
                  <span className="mt-1 block">
                    {confirmResult.rulesCreated} kategoriregel(er) skapade.
                  </span>
                )}
                {confirmResult.transferRulesCreated > 0 && (
                  <span className="mt-1 block">
                    {confirmResult.transferRulesCreated} överföringsregel(er) skapade.
                  </span>
                )}
              </>
            )}
          </p>
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="mt-5 w-full"
            onClick={() => {
              store.getState().resetWorkflow();
              setDone(false);
              setConfirmResult(null);
              if (fileRef.current) fileRef.current.value = '';
            }}
          >
            Importera fler
          </Button>
        </div>
      </div>
    );
  }

  const pending = pendingImportQuery.data;

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Importera transaktioner</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Läs in en CSV-fil från banken och granska innan något sparas.
        </p>
      </header>

      {!batchId && !isResuming && pending?.reviewState && (
        <PendingImportNotice
          tone="neutral"
          icon="📋"
          title="Det finns en sparad import att fortsätta med."
          meta={`Fil: ${pending.fileName} · ${pending.transactionCount} transaktioner · ${new Date(pending.createdAt).toLocaleString('sv-SE')}`}
        >
          <Button
            type="button"
            size="sm"
            className="flex-1 sm:flex-none"
            onClick={() => setIsResuming(true)}
          >
            Fortsätt import
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="flex-1 sm:flex-none"
            onClick={() => {
              if (pending?.id) {
                discardMutation.mutate({ batchId: pending.id });
              }
            }}
            disabled={discardMutation.isPending}
          >
            {discardMutation.isPending ? 'Kasserar…' : 'Kassera'}
          </Button>
        </PendingImportNotice>
      )}

      {isResuming && !batchId && (
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <p className="animate-pulse text-sm text-muted-foreground">Laddar sparad import…</p>
        </div>
      )}

      {!batchId && pending && !pending.reviewState && (
        <PendingImportNotice
          tone="warning"
          icon="⚠️"
          title="Det finns en pågående import som inte har bekräftats."
          meta={`Fil: ${pending.fileName} · ${pending.transactionCount} transaktioner · ${new Date(pending.createdAt).toLocaleString('sv-SE')}`}
        >
          {pending.transactions && (
            <Button
              type="button"
              size="sm"
              className="flex-1 sm:flex-none"
              onClick={() => {
                const data = pendingImportQuery.data;
                if (!data?.transactions) return;
                store.getState().initializeFromImport({
                  batchId: data.id,
                  transactions: data.transactions as ImportedTxn[],
                  accountProposals: (data.accountProposals ?? []) as AccountProposal[],
                  skippedReservedCount: data.skippedReservedCount ?? 0,
                });
              }}
            >
              Granska import
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="flex-1 bg-card sm:flex-none"
            onClick={() => {
              if (pending?.id) {
                discardMutation.mutate({ batchId: pending.id });
              }
            }}
            disabled={discardMutation.isPending}
          >
            {discardMutation.isPending ? 'Kasserar…' : 'Kassera'}
          </Button>
        </PendingImportNotice>
      )}

      {!batchId && !pending && (
        <div className="space-y-4 rounded-2xl border border-border bg-card p-4 sm:p-5">
          <div>
            <label htmlFor="import-member" className="mb-1.5 block text-sm font-medium">
              Vems transaktioner?
            </label>
            <select
              id="import-member"
              value={selectedMemberId}
              onChange={(e) => setSelectedMemberId(e.target.value)}
              className="min-h-11 w-full rounded-lg border border-input bg-card px-3 py-2 text-base outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 sm:min-h-0 sm:w-auto sm:text-sm"
            >
              <option value="">Välj…</option>
              <option value="__shared__">Gemensamt konto</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName}
                </option>
              ))}
            </select>
          </div>

          <div
            className={cn(
              'rounded-2xl border-2 border-dashed p-6 text-center transition-colors sm:p-8',
              hasSelectedMember
                ? 'cursor-pointer border-primary/40 hover:bg-muted/60'
                : 'border-border opacity-50',
            )}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.txt"
              disabled={!hasSelectedMember}
              onChange={handleFileUpload}
              className="hidden"
              id="file-input"
            />
            <label
              htmlFor="file-input"
              className={cn(
                'flex flex-col items-center',
                hasSelectedMember ? 'cursor-pointer' : 'cursor-not-allowed',
              )}
            >
              <span
                aria-hidden="true"
                className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground"
              >
                <FileUp className="size-5" />
              </span>
              <span className="mt-3 font-medium">Välj Nordea CSV-fil</span>
              <span className="mt-1 text-xs text-muted-foreground">
                Exportera från Nordea Netbank · Kontotransaktioner
              </span>
            </label>
          </div>

          {importMutation.isPending && (
            <p className="text-center text-sm text-muted-foreground">Analyserar fil…</p>
          )}
          {importMutation.error && (
            <p className="rounded-lg bg-negative-soft px-3 py-2 text-sm text-negative">
              {importMutation.error.message}
            </p>
          )}
        </div>
      )}

      {batchId && transactions.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground tabular-nums">
                {transactions.length}
              </span>{' '}
              transaktioner ·{' '}
              <span className="tabular-nums">
                {transactions.filter((t) => overrides[t.id]?.skip).length}
              </span>{' '}
              markerade som dubblett/hoppa
              {skippedReservedCount > 0 && (
                <span className="mt-0.5 block text-xs text-warning">
                  {skippedReservedCount} reserverade kortköp hoppades över (importeras automatiskt
                  när de bokförs)
                </span>
              )}
              <span className="mt-2 flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={saveProgress}
                  disabled={saveStatus === 'saving' || !batchId}
                >
                  {saveStatus === 'saving' ? 'Sparar…' : 'Spara utkast'}
                </Button>
                <span
                  role="status"
                  className={cn(
                    'text-xs',
                    saveStatus === 'error' ? 'text-warning' : 'text-muted-foreground',
                  )}
                >
                  {saveStatus === 'saved' &&
                    lastSavedAt &&
                    `Sparat ${formatRelativeTime(lastSavedAt)}`}
                  {saveStatus === 'error' && 'Kunde inte spara, försök igen.'}
                </span>
              </span>
            </div>
            <Button
              type="button"
              size="lg"
              onClick={handleConfirm}
              disabled={confirmMutation.isPending}
              className="w-full shrink-0 sm:w-auto"
            >
              {confirmMutation.isPending ? 'Sparar…' : 'Bekräfta import'}
            </Button>
          </div>

          {/* Section 1: Account proposals */}
          <AccountProposalCard store={store} members={members} />

          {/* Section 2: Transaction table */}
          <ImportPreviewTable
            store={store}
            categories={allCategories}
            groups={allGroups}
            onCreateCategory={handleCreateCategory}
          />

          {/* Section 3: Rule summary */}
          <RuleSuggestionRow store={store} rules={ruleSummary} />
        </div>
      )}
    </div>
  );
}

function PendingImportNotice({
  tone,
  icon,
  title,
  meta,
  children,
}: {
  tone: 'neutral' | 'warning';
  icon: string;
  title: string;
  meta: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-start gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:p-5',
        tone === 'warning' ? 'border-warning/25 bg-warning-soft' : 'border-border bg-card',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-full text-lg',
          tone === 'warning' ? 'bg-card' : 'bg-muted',
        )}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-sm font-medium',
            tone === 'warning' ? 'text-warning' : 'text-foreground',
          )}
        >
          {title}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">{meta}</p>
      </div>
      <div className="flex w-full shrink-0 gap-2 sm:w-auto">{children}</div>
    </div>
  );
}
